/**
 * 📨 Postacı — Toast Bildirim Sağlayıcısı
 * In-app toast bildirimleri göstermek için global context.
 */
import React, { createContext, useContext, useState, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle, WarningCircle, XCircle, Info, X } from '@phosphor-icons/react'

// ============================================
// TYPES
// ============================================

type ToastType = 'success' | 'warning' | 'error' | 'info'

interface ToastAction {
  label: string
  onClick: () => void
}

interface Toast {
  id: string
  type: ToastType
  title: string
  message: string
  action?: ToastAction
}

interface ToastOptions {
  action?: ToastAction
}

interface ToastContextType {
  showSuccess: (title: string, message: string, options?: ToastOptions) => void
  showWarning: (title: string, message: string, options?: ToastOptions) => void
  showError: (title: string, message: string, options?: ToastOptions) => void
  showInfo: (title: string, message: string, options?: ToastOptions) => void
  clearAll: () => void
}

// ============================================
// CONSTANTS
// ============================================

const AUTO_DISMISS_MS = 4000
const MAX_VISIBLE_TOASTS = 3

const TOAST_STYLES: Record<
  ToastType,
  { icon: typeof Info; border: string; iconColor: string }
> = {
  success: {
    icon: CheckCircle,
    border: 'border-emerald-500/30',
    iconColor: 'text-emerald-500',
  },
  warning: {
    icon: WarningCircle,
    border: 'border-amber-500/30',
    iconColor: 'text-amber-500',
  },
  error: { 
    icon: XCircle, 
    border: 'border-rose-500/30', 
    iconColor: 'text-rose-500' 
  },
  info: { 
    icon: Info, 
    border: 'border-blue-500/30', 
    iconColor: 'text-blue-500' 
  },
}

// ============================================
// CONTEXT
// ============================================

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function useToast(): ToastContextType {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

// ============================================
// PROVIDER
// ============================================

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((type: ToastType, title: string, message: string, options?: ToastOptions) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    const newToast: Toast = { id, type, title, message, ...options }

    setToasts(prev => [...prev.slice(-(MAX_VISIBLE_TOASTS - 1)), newToast])

    // Eğer aksiyon varsa daha uzun süre ekranda kalsın
    const dismissMs = options?.action ? AUTO_DISMISS_MS + 3000 : AUTO_DISMISS_MS

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, dismissMs)
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const showSuccess = useCallback(
    (title: string, message: string, options?: ToastOptions) => addToast('success', title, message, options),
    [addToast]
  )
  const showWarning = useCallback(
    (title: string, message: string, options?: ToastOptions) => addToast('warning', title, message, options),
    [addToast]
  )
  const showError = useCallback(
    (title: string, message: string, options?: ToastOptions) => addToast('error', title, message, options),
    [addToast]
  )
  const showInfo = useCallback(
    (title: string, message: string, options?: ToastOptions) => addToast('info', title, message, options),
    [addToast]
  )
  const clearAll = useCallback(() => setToasts([]), [])

  const contextValue = React.useMemo(
    () => ({
      showSuccess,
      showWarning,
      showError,
      showInfo,
      clearAll,
    }),
    [showSuccess, showWarning, showError, showInfo, clearAll]
  )

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* Toast Container — Yığılma payı (paddingTop) safe-area üzerine eklendi */}
      <div 
        className="fixed left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-3 w-[calc(100%-32px)] max-w-[380px] pointer-events-none" 
        style={{ top: 'calc(env(safe-area-inset-top, 1rem) + 1rem)' }}
      >
        <AnimatePresence mode="popLayout">
          {toasts.map(toast => {
            const style = TOAST_STYLES[toast.type]
            const Icon = style.icon

            return (
              <motion.div
                key={toast.id}
                layout
                initial={{ opacity: 0, y: -50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                transition={{ type: 'spring', stiffness: 500, damping: 40, mass: 1 }}
                className={`
                                    bg-background/80 ${style.border}
                                    border rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-xl
                                    pointer-events-auto flex items-start gap-3
                                `}
              >
                <Icon size={20} weight="fill" className={`${style.iconColor} shrink-0 mt-[2px]`} />
                <div className="flex-1 min-w-0 pb-0.5">
                  <p className="text-sm font-semibold text-foreground tracking-tight leading-none">{toast.title}</p>
                  <p className="text-[13px] text-muted-foreground mt-1.5 leading-snug line-clamp-2">
                    {toast.message}
                  </p>
                  {toast.action && (
                    <button
                      onClick={() => {
                        toast.action!.onClick()
                        removeToast(toast.id)
                      }}
                      className={`mt-2.5 inline-flex items-center text-xs font-bold leading-none ${style.iconColor} hover:opacity-80 active:scale-95 transition-all`}
                    >
                      {toast.action.label}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => removeToast(toast.id)}
                  className="shrink-0 p-1 rounded-full hover:bg-black/5 transition-colors"
                >
                  <X size={14} className="text-muted-foreground" />
                </button>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  )
}

export default ToastProvider
